import { useRef, useState, type ReactNode } from "react";
import { Pencil, X } from "lucide-react";
import { Icon } from "@cntrl/ui";
import { AvatarCropDialog } from "./AvatarCropDialog.js";
import styles from "./AvatarEditor.module.scss";

export interface AvatarEditorProps {
  /** The current picture, already resolved to something an `<img>` can take. Null ⇒ the placeholder. */
  src?: string | null;
  /** What fills the control with no picture — initials for a person, a mark for a group. */
  placeholder: ReactNode;
  /** A person is a circle; anything that is not somebody's face is a rounded square. */
  shape?: "circle" | "rounded";
  /** The control's accessible name — it is a button, and a picture alone announces nothing. */
  label: string;
  /** Disables the control and says so in the veil while an upload is in flight. */
  busy?: boolean;
  /** The CROPPED square (a 512² JPEG). The caller owns the upload — the transports differ per surface. */
  onPicked: (file: File) => void;
  /**
   * Clear the picture. Omit it where there is nothing to clear — the ✕ appears only when this is supplied,
   * so "is there a picture to remove" stays the CALLER's question rather than being guessed from `src`
   * (a chat's `src` falls back to the show's flyer, which is not the chat's to delete).
   */
  onRemove?: () => void;
  /** Names the ✕ — "Remove picture" is rarely specific enough to be the only thing a reader hears. */
  removeLabel?: string;
}

/**
 * THE AVATAR EDITOR — one control, wherever a picture is replaceable (PO, 2026-09-11: "make it like the
 * user profile's avatar editor, we need a consistent design language").
 *
 * The treatment is the profile's own, unchanged (PO ruling 2026-08-20): the picture top-middle, the control
 * IS the picture — hover shows a veil reading "Replace", the SE badge carries the pencil, and a pick goes
 * through the CROP step rather than uploading straight. It was extracted here the moment a second surface
 * wanted it, because a hover veil and a badge offset living in two stylesheets are two things that drift.
 *
 * **The upload is the CALLER's**, and deliberately so: a person's avatar is a multipart `PUT /me/avatar`
 * while a chat's rides the band media pipeline and a PATCH. What has to be identical is the gesture, not
 * the transport — so this owns the button, the veil, the badge, the file input and the crop, and hands back
 * a squared 512² JPEG.
 *
 * **A pick uploads IMMEDIATELY; it does not ride a Save button.** That is the profile's rule and it is the
 * reason the crop dialog can close straight into an upload: the picture is a thing you replaced, not a
 * field you are editing. Any form fields beside it still save on their own.
 */
export function AvatarEditor({
  src,
  placeholder,
  shape = "circle",
  label,
  busy,
  onPicked,
  onRemove,
  removeLabel = "Remove picture",
}: AvatarEditorProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);

  return (
    <div className={styles.wrap}>
      {/* The FRAME exists so the ✕ can sit over the picture: it cannot be INSIDE the control, which is
          itself a <button>, and a button inside a button is not markup a browser will honour. Siblings in a
          positioned frame, with the hover keyed on the frame so moving onto the ✕ does not dismiss it. */}
      <div className={styles.frame}>
        <button
          type="button"
          className={styles.control}
          data-shape={shape}
          aria-label={label}
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {src ? (
            <img className={styles.img} src={src} alt="" data-photo="" />
          ) : (
            <span className={styles.placeholder} aria-hidden="true">
              {placeholder}
            </span>
          )}
          <span className={styles.veil} aria-hidden="true">
            {busy ? "Uploading…" : "Replace"}
          </span>
          <span className={styles.badge} aria-hidden="true">
            <Icon icon={Pencil} size={16} />
          </span>
        </button>

        {onRemove && (
          <button
            type="button"
            className={styles.remove}
            aria-label={removeLabel}
            disabled={busy}
            onClick={onRemove}
          >
            <Icon icon={X} size={16} />
          </button>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className={styles.hiddenInput}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared BEFORE anything else, so picking the same file twice still fires a change event.
          e.target.value = "";
          if (!file) return;
          // The crop step, not a straight upload: it squares the picture and is what makes big originals
          // workable at all — the export leaves as a 512² JPEG regardless of source size.
          setCropFile(file);
        }}
      />

      {cropFile && (
        <AvatarCropDialog
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onCropped={(blob) => {
            setCropFile(null);
            onPicked(new File([blob], "avatar.jpg", { type: "image/jpeg" }));
          }}
        />
      )}
    </div>
  );
}
